import React from "react";
import { AlertCircle, CheckCircle, AlertTriangle, Info } from "lucide-react";
import type { AlertType } from "./Alert";
import Button from "../button/Button";

interface AlertViewProps {
  alert: AlertType;
  onDismiss?: () => void;
}

/**
 * Full-screen alert view that replaces the main content area
 * Used for displaying errors or important messages that require user attention
 */
export const AlertView: React.FC<AlertViewProps> = ({ alert, onDismiss }) => {
  const { variant = "info", title, message } = alert;

  const variantConfig = {
    success: {
      icon: CheckCircle,
      iconColor: "text-success-500",
      bgColor: "bg-success-50 dark:bg-success-500/10",
      borderColor: "border-success-200 dark:border-success-500/30",
    },
    error: {
      icon: AlertCircle,
      iconColor: "text-error-500",
      bgColor: "bg-error-50 dark:bg-error-500/10",
      borderColor: "border-error-200 dark:border-error-500/30",
    },
    warning: {
      icon: AlertTriangle,
      iconColor: "text-warning-500",
      bgColor: "bg-warning-50 dark:bg-warning-500/10",
      borderColor: "border-warning-200 dark:border-warning-500/30",
    },
    info: {
      icon: Info,
      iconColor: "text-blue-500",
      bgColor: "bg-blue-50 dark:bg-blue-500/10",
      borderColor: "border-blue-200 dark:border-blue-500/30",
    },
  };

  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div
        className={`relative max-w-md w-full rounded-2xl border ${config.borderColor} ${config.bgColor} p-8 shadow-sm`}
      >
        <div className="flex flex-col items-center text-center">
          <div className={`mb-4 rounded-full p-3 ${config.bgColor}`}>
            <Icon size={48} className={config.iconColor} />
          </div>

          <h2 className="mb-2 text-xl font-semibold text-gray-900 dark:text-white">
            {title}
          </h2>

          <p className="mb-6 text-gray-600 dark:text-gray-400">{message}</p>

          {onDismiss && (
            <Button onClick={onDismiss} variant="primary">
              Dismiss
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
